import React, { useState, useContext, useEffect, useMemo, useRef } from "react";
import Swal from "sweetalert2";

import AuthService from "../../../../services/Admin/AuthService";
import MessagingService from "../../../../services/Admin/MessagingService";
import HttpError from "../../../../utill/HttpError";
import LoadingContext from "../../../../store/loading-context";

/* ------------------------------------------------------------------ */
/*  constants                                                          */
/* ------------------------------------------------------------------ */
const RECOMMENDED_W = 1080;
const RECOMMENDED_H = 540;
const ASPECT_TOLERANCE = 0.12;

const HISTORY_LIMIT = 30;

const MAX_UPLOAD_BYTES = 2 * 1024 * 1024; // 2 MB
const ALLOWED_MIME = ["image/jpeg", "image/jpg", "image/png"];
const ALLOWED_EXT = ["jpg", "jpeg", "png"];

const TARGETS = [
  {
    key: "topic",
    label: "All subscribers",
    hint: "Send to everyone subscribed to the app's main topic.",
    icon: "fas fa-users",
  },
  {
    key: "tokens",
    label: "All devices",
    hint: "Send to every registered device token in the database.",
    icon: "fas fa-mobile-alt",
  },
  {
    key: "user",
    label: "Specific user",
    hint: "Send to one user by their user ID.",
    icon: "fas fa-user",
  },
];

const EMPTY = { title: "", body: "", image: "" };

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/* ------------------------------------------------------------------ */
/*  time helper                                                        */
/* ------------------------------------------------------------------ */
const formatWhen = (iso) => {
  const then = new Date(iso);
  if (isNaN(then.getTime())) return "";
  const diff = Math.floor((Date.now() - then.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return then.toLocaleDateString();
};

/* ------------------------------------------------------------------ */
/*  upload via backend proxy                                           */
/* ------------------------------------------------------------------ */
const uploadImage = async (file) => {
  const token = AuthService.getAccessToken();

  /* calls MessagingService.imgToLink(token, file) — backend endpoint */
  const json = await MessagingService.imgToLink(token, file);

  if (!json || json.status !== "SUCCESS" || !json.data || !json.data.url) {
    const message =
      (json && json.message) || "Upload failed. Please try again.";
    throw new Error(message);
  }

  return json.data.url;
};

/* local pre-check before hitting the server */
const precheckFile = (file) =>
  new Promise((resolve) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext) || !ALLOWED_MIME.includes(file.type)) {
      return resolve({
        ok: false,
        reason: "Only JPG, JPEG, or PNG images are allowed.",
      });
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      return resolve({
        ok: false,
        reason: `Image is ${formatBytes(file.size)}. Max upload is 2 MB.`,
      });
    }

    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        ok: true,
        width: img.naturalWidth,
        height: img.naturalHeight,
        size: file.size,
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ ok: false, reason: "Could not read image dimensions." });
    };
    img.src = url;
  });

/* ------------------------------------------------------------------ */
/*  component                                                          */
/* ------------------------------------------------------------------ */
const PushNotification = ({ onSent }) => {
  const loadingCtx = useContext(LoadingContext);
  const searchRef = useRef(null);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState(EMPTY);
  const [target, setTarget] = useState("topic");
  const [userId, setUserId] = useState("");
  const [sending, setSending] = useState(false);

  const [imageStatus, setImageStatus] = useState("idle");
  const [imageMeta, setImageMeta] = useState(null);
  const [imageMessage, setImageMessage] = useState("");

  /* image source mode: "url" | "upload" */
  const [imageMode, setImageMode] = useState("url");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [localPreview, setLocalPreview] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");

  /* ---------- history (server-backed) ---------- */
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [filterTarget, setFilterTarget] = useState("all");

  /* ---------- derived: filtered history ---------- */
  const filteredHistory = useMemo(() => {
    const q = search.trim().toLowerCase();
    return history.filter((h) => {
      if (filterTarget !== "all" && h.target !== filterTarget) return false;
      if (!q) return true;
      return (
        (h.title || "").toLowerCase().includes(q) ||
        (h.body || "").toLowerCase().includes(q) ||
        (h.userId && h.userId.toLowerCase().includes(q))
      );
    });
  }, [history, search, filterTarget]);

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  /* ---------- fetch history from server ---------- */
  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const token = AuthService.getAccessToken();
      const json = await MessagingService.getPushHistory(token, HISTORY_LIMIT);
      if (json && json.status === "SUCCESS" && json.data && json.data.items) {
        setHistory(json.data.items);
      }
    } catch (_) {
      /* leave the list empty on network error */
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- keyboard ---------- */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        sendHandler();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, target, userId, imageStatus, uploading]);

  /* ---------- validate image URL (only in url mode) ---------- */
  useEffect(() => {
    if (imageMode !== "url") return;

    const url = form.image.trim();

    if (!url) {
      setImageStatus("idle");
      setImageMeta(null);
      setImageMessage("");
      return;
    }

    let parsed;
    try {
      parsed = new URL(url);
    } catch (_) {
      setImageStatus("error");
      setImageMeta(null);
      setImageMessage("Not a valid URL.");
      return;
    }

    const ext = (parsed.pathname.split(".").pop() || "").toLowerCase();
    if (!["jpg", "jpeg", "png"].includes(ext)) {
      setImageStatus("error");
      setImageMeta(null);
      setImageMessage("URL must point to a .jpg, .jpeg or .png image.");
      return;
    }

    setImageStatus("loading");
    setImageMessage("Checking image…");

    const img = new Image();
    let cancelled = false;

    img.onload = () => {
      if (cancelled) return;
      const { naturalWidth: width, naturalHeight: height } = img;
      const ratio = width / height;
      const targetRatio = RECOMMENDED_W / RECOMMENDED_H;
      const deviation = Math.abs(ratio - targetRatio) / targetRatio;

      setImageMeta({ width, height });

      if (width < 300 || height < 150) {
        setImageStatus("error");
        setImageMessage(
          `Image is too small (${width}×${height}px). Minimum 300×150px.`,
        );
        return;
      }
      if (deviation > ASPECT_TOLERANCE) {
        setImageStatus("warning");
        setImageMessage(
          `${width}×${height}px — off-ratio. Recommended ${RECOMMENDED_W}×${RECOMMENDED_H}px (2:1). May be cropped.`,
        );
        return;
      }
      setImageStatus("ok");
      setImageMessage(`${width}×${height}px — looks good.`);
    };

    img.onerror = () => {
      if (cancelled) return;
      setImageStatus("error");
      setImageMeta(null);
      setImageMessage("Could not load image from that URL.");
    };

    img.src = url;
    return () => {
      cancelled = true;
    };
  }, [form.image, imageMode]);

  /* ---------- cleanup local preview url ---------- */
  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  /* ---------- switch mode ---------- */
  const switchImageMode = (mode) => {
    if (mode === imageMode) return;
    setImageMode(mode);
    setUploadError("");

    if (mode === "url") {
      if (localPreview) URL.revokeObjectURL(localPreview);
      setLocalPreview("");
      setUploadedFileName("");
    } else {
      setField("image", "");
      setImageStatus("idle");
      setImageMeta(null);
      setImageMessage("");
    }
  };

  /* ---------- handle file ---------- */
  const handleFile = async (file) => {
    if (!file) return;

    setUploadError("");
    setUploadedFileName(file.name);

    const pre = await precheckFile(file);

    if (!pre.ok) {
      setUploadError(pre.reason);
      setUploadedFileName("");
      Swal.fire("Invalid image", pre.reason, "error");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (localPreview) URL.revokeObjectURL(localPreview);
    const preview = URL.createObjectURL(file);
    setLocalPreview(preview);
    setImageMeta({ width: pre.width, height: pre.height });

    setUploading(true);
    setImageStatus("loading");
    setImageMessage("Uploading image…");

    try {
      const url = await uploadImage(file);

      setField("image", url);
      setImageStatus("ok");
      setImageMessage(`Uploaded · ${pre.width}×${pre.height}px`);
    } catch (err) {
      setUploadError(err.message || "Upload failed.");
      setImageStatus("error");
      setImageMessage("Upload failed. Try a different image.");
      setField("image", "");
      if (localPreview) URL.revokeObjectURL(localPreview);
      setLocalPreview("");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onFilePick = (e) => {
    const file = e.target.files && e.target.files[0];
    handleFile(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    handleFile(file);
  };

  const clearImage = () => {
    if (localPreview) URL.revokeObjectURL(localPreview);
    setLocalPreview("");
    setUploadedFileName("");
    setUploadError("");
    setImageStatus("idle");
    setImageMeta(null);
    setImageMessage("");
    setField("image", "");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /* ---------- validation ---------- */
  const validate = () => {
    if (!form.title.trim()) {
      Swal.fire("Opps!", "Notification title is required.", "warning");
      return false;
    }
    if (!form.body.trim()) {
      Swal.fire("Opps!", "Notification message body is required.", "warning");
      return false;
    }
    if (target === "user" && !userId.trim()) {
      Swal.fire("Opps!", "Please enter a user ID.", "warning");
      return false;
    }
    if (uploading) {
      Swal.fire(
        "Opps!",
        "Please wait for the image to finish uploading.",
        "info",
      );
      return false;
    }
    if (imageMode === "upload" && uploadError) {
      Swal.fire("Opps!", uploadError, "warning");
      return false;
    }
    if (form.image.trim() && imageStatus === "error") {
      Swal.fire("Opps!", imageMessage || "Invalid image URL.", "warning");
      return false;
    }
    return true;
  };

  /* ---------- history: clear ---------- */
  const clearHistory = async () => {
    const confirmed = await Swal.fire({
      title: "Clear history?",
      text: "This removes the list from your account. It does not affect sent notifications.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Clear",
      confirmButtonColor: "#e3342f",
    });
    if (!confirmed.isConfirmed) return;

    const previous = history;
    setHistory([]);
    setSearch("");
    setFilterTarget("all");

    try {
      const token = AuthService.getAccessToken();
      const json = await MessagingService.clearPushHistory(token);
      if (!json || json.status !== "SUCCESS") {
        throw new Error((json && json.message) || "Failed to clear history.");
      }
    } catch (err) {
      setHistory(previous);
      Swal.fire("Opps!", err.message || "Could not clear history.", "error");
    }
  };

  /* ---------- history: remove one ---------- */
  const removeHistoryItem = async (id) => {
    const previous = history;
    setHistory((prev) => prev.filter((h) => h.id !== id));

    try {
      const token = AuthService.getAccessToken();
      const json = await MessagingService.removePushHistory(token, id);
      if (!json || json.status !== "SUCCESS") {
        throw new Error((json && json.message) || "Failed to remove entry.");
      }
    } catch (err) {
      setHistory(previous);
      Swal.fire("Opps!", err.message || "Could not remove entry.", "error");
    }
  };

  /* ---------- history: reuse ---------- */
  const reuseEntry = (entry) => {
    setForm({
      title: entry.title,
      body: entry.body,
      image: entry.image || "",
    });
    setTarget(entry.target);
    setUserId(entry.userId || "");
    setImageMode("url");
    if (localPreview) URL.revokeObjectURL(localPreview);
    setLocalPreview("");
    setUploadedFileName("");
    setUploadError("");
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "info",
      title: "Loaded into form",
      showConfirmButton: false,
      timer: 1400,
    });
  };

  const resetForm = () => {
    setForm(EMPTY);
    setUserId("");
    clearImage();
  };

  /* ---------- send ---------- */
  const sendHandler = async () => {
    if (!validate()) return;

    if (target !== "user") {
      const confirmed = await Swal.fire({
        title: "Send to everyone?",
        text:
          target === "topic"
            ? "This will push the notification to all topic subscribers."
            : "This will push the notification to every registered device.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, send",
        confirmButtonColor: "#17a2b8",
      });
      if (!confirmed.isConfirmed) return;
    }

    loadingCtx.startLoading();
    setSending(true);

    const title = form.title.trim();
    const body = form.body.trim();
    const image = form.image.trim();

    try {
      const token = AuthService.getAccessToken();

      let responseData;
      if (target === "topic") {
        responseData = await MessagingService.sendToAllTopic(
          token,
          title,
          body,
          image,
        );
      } else if (target === "tokens") {
        responseData = await MessagingService.sendToAllTokens(
          token,
          title,
          body,
          image,
        );
      } else {
        responseData = await MessagingService.sendToUser(
          token,
          userId.trim(),
          title,
          body,
          image,
        );
      }

      const status = responseData.status;
      const message = responseData.message;

      if (status === "SUCCESS") {
        Swal.fire("Sent!", message || "Notification delivered.", "success");

        /* reset the form, then reload the list from the server */
        setForm(EMPTY);
        setUserId("");
        clearImage();
        await loadHistory();

        if (onSent) onSent(responseData);
      } else {
        Swal.fire("Opps!", message || "Could not send notification.", "error");
      }
    } catch (error) {
      Swal.fire("Opps!", HttpError.getError(error), "error");
    } finally {
      loadingCtx.stopLoading();
      setSending(false);
    }
  };

  /* ---------- preview image source ---------- */
  const previewSrc =
    imageMode === "upload" && localPreview ? localPreview : form.image;

  /* ---------- render ---------- */
  return (
    <React.Fragment>
      <style>{styles}</style>

      <div className="pn-wrap">
        {/* --------------------- LEFT: form --------------------- */}
        <div className="pn-form">
          <div className="card pn-card">
            <div className="card-header pn-card__header">
              <div>
                <h3 className="card-title mb-0">
                  <i className="fas fa-bell mr-2" />
                  Push Notification
                </h3>
              </div>
              <div className="pn-header__meta">
                <span className="pn-kbd-hint">
                  <kbd>Ctrl</kbd>+<kbd>Enter</kbd> send
                </span>
              </div>
            </div>

            <div className="card-body">
              {/* target */}
              <div className="pn-label">Send to</div>
              <div className="pn-targets">
                {TARGETS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className={
                      "pn-target" +
                      (target === t.key ? " pn-target--active" : "")
                    }
                    onClick={() => setTarget(t.key)}
                  >
                    <i className={t.icon} />
                    <span className="pn-target__label">{t.label}</span>
                  </button>
                ))}
              </div>
              <p className="pn-hint">
                {TARGETS.find((t) => t.key === target)?.hint}
              </p>

              {target === "user" && (
                <div className="form-group mt-3">
                  <label htmlFor="pn-user-id">User ID</label>
                  <input
                    id="pn-user-id"
                    type="text"
                    className="form-control"
                    placeholder="e.g. 64f8c2a1b4e9d10012a3b456"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                  />
                </div>
              )}

              {/* title */}
              <div className="form-group mt-3">
                <label htmlFor="pn-title">
                  Title <span className="pn-req">*</span>
                </label>
                <input
                  id="pn-title"
                  type="text"
                  className="form-control"
                  placeholder="e.g. 🎉 Weekend bonus is live!"
                  maxLength={80}
                  value={form.title}
                  onChange={(e) => setField("title", e.target.value)}
                />
                <small
                  className={
                    "pn-charcount" +
                    (form.title.length > 70 ? " pn-charcount--warn" : "")
                  }
                >
                  {form.title.length}/80
                </small>
              </div>

              {/* body */}
              <div className="form-group">
                <label htmlFor="pn-body">
                  Message <span className="pn-req">*</span>
                </label>
                <textarea
                  id="pn-body"
                  className="form-control"
                  rows={4}
                  placeholder="e.g. Top up any amount this weekend and get 10% extra data."
                  maxLength={240}
                  value={form.body}
                  onChange={(e) => setField("body", e.target.value)}
                />
                <small
                  className={
                    "pn-charcount" +
                    (form.body.length > 200 ? " pn-charcount--warn" : "")
                  }
                >
                  {form.body.length}/240
                </small>
              </div>

              {/* image section */}
              <div className="form-group">
                <label>
                  Banner image <span className="pn-optional">(optional)</span>
                </label>

                <div className="pn-img-toggle">
                  <button
                    type="button"
                    className={
                      "pn-img-toggle__btn" +
                      (imageMode === "url" ? " is-active" : "")
                    }
                    onClick={() => switchImageMode("url")}
                    disabled={uploading}
                  >
                    <i className="fas fa-link mr-1" /> Paste URL
                  </button>
                  <button
                    type="button"
                    className={
                      "pn-img-toggle__btn" +
                      (imageMode === "upload" ? " is-active" : "")
                    }
                    onClick={() => switchImageMode("upload")}
                    disabled={uploading}
                  >
                    <i className="fas fa-cloud-upload-alt mr-1" /> Upload file
                  </button>
                </div>

                {imageMode === "url" && (
                  <>
                    <div className="pn-input-wrap">
                      <input
                        id="pn-image"
                        type="text"
                        className={
                          "form-control pn-input" +
                          (imageStatus === "error" ? " pn-input--error" : "") +
                          (imageStatus === "warning" ? " pn-input--warn" : "") +
                          (imageStatus === "ok" ? " pn-input--ok" : "")
                        }
                        placeholder="https://example.com/banner.jpg"
                        value={form.image}
                        onChange={(e) => setField("image", e.target.value)}
                      />
                      {imageStatus !== "idle" && (
                        <span
                          className={
                            "pn-input__icon pn-input__icon--" + imageStatus
                          }
                        >
                          {imageStatus === "loading" && (
                            <i className="fas fa-spinner fa-spin" />
                          )}
                          {imageStatus === "ok" && (
                            <i className="fas fa-check-circle" />
                          )}
                          {imageStatus === "warning" && (
                            <i className="fas fa-exclamation-triangle" />
                          )}
                          {imageStatus === "error" && (
                            <i className="fas fa-times-circle" />
                          )}
                        </span>
                      )}
                    </div>

                    {imageMessage ? (
                      <small
                        className={"pn-img-msg pn-img-msg--" + imageStatus}
                      >
                        {imageMessage}
                      </small>
                    ) : (
                      <small className="pn-img-msg pn-img-msg--hint">
                        JPG, JPEG or PNG · recommended 1080×540px (2:1 aspect).
                      </small>
                    )}
                  </>
                )}

                {imageMode === "upload" && (
                  <>
                    <div
                      className={
                        "pn-drop" + (uploading ? " pn-drop--busy" : "")
                      }
                      onClick={() =>
                        !uploading && fileInputRef.current?.click()
                      }
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={onDrop}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                        onChange={onFilePick}
                        hidden
                      />

                      {!localPreview ? (
                        <div className="pn-drop__empty">
                          <i className="fas fa-cloud-upload-alt" />
                          <div className="pn-drop__title">
                            Drop image here or click to browse
                          </div>
                          <div className="pn-drop__hint">
                            JPG, JPEG or PNG · max 2 MB · recommended 1080×540px
                          </div>
                        </div>
                      ) : (
                        <div className="pn-drop__filled">
                          <div className="pn-drop__thumb">
                            <img src={localPreview} alt="" />
                            {uploading && (
                              <div className="pn-drop__overlay">
                                <i className="fas fa-spinner fa-spin" />
                                <span>Uploading…</span>
                              </div>
                            )}
                          </div>
                          <div className="pn-drop__info">
                            <div className="pn-drop__name">
                              {uploadedFileName}
                            </div>
                            {imageMeta && (
                              <div className="pn-drop__meta">
                                {imageMeta.width}×{imageMeta.height}px
                              </div>
                            )}
                            <div className="pn-drop__status">
                              {uploading && (
                                <span className="pn-drop__status--busy">
                                  <i className="fas fa-spinner fa-spin mr-1" />
                                  Uploading…
                                </span>
                              )}
                              {!uploading && form.image && (
                                <span className="pn-drop__status--ok">
                                  <i className="fas fa-check-circle mr-1" />
                                  Uploaded · URL ready
                                </span>
                              )}
                              {!uploading && uploadError && (
                                <span className="pn-drop__status--err">
                                  <i className="fas fa-times-circle mr-1" />
                                  {uploadError}
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              className="pn-drop__remove"
                              onClick={(e) => {
                                e.stopPropagation();
                                clearImage();
                              }}
                            >
                              <i className="far fa-trash-alt mr-1" /> Remove
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <small className="pn-img-msg pn-img-msg--hint">
                      File is uploaded to your server and the public URL is used
                      for the notification.
                    </small>
                  </>
                )}
              </div>

              {/* footer */}
              <div className="pn-footer">
                <span className="pn-target-summary">
                  <i className="fas fa-paper-plane mr-1" />
                  Sending to:{" "}
                  <strong>
                    {TARGETS.find((t) => t.key === target)?.label}
                  </strong>
                </span>

                <div className="pn-footer__actions">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={resetForm}
                    disabled={sending}
                  >
                    <i className="fas fa-undo mr-1" /> Reset
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={sendHandler}
                    disabled={sending || uploading}
                  >
                    {sending ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-1" /> Sending…
                      </>
                    ) : uploading ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-1" /> Uploading…
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane mr-1" /> Send
                        Notification
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --------------------- RIGHT: preview + history --------------------- */}
        <aside className="pn-side">
          <div className="pn-side__label">
            <i className="fas fa-eye mr-1" /> Live preview
          </div>

          <div className="pn-phone">
            <div className="pn-phone__notch" />
            <div className="pn-phone__screen">
              <div className="pn-phone__statusbar">
                <span>9:41</span>
                <span className="pn-phone__statusbar-icons">
                  <i className="fas fa-signal" />
                  <i className="fas fa-wifi" />
                  <i className="fas fa-battery-three-quarters" />
                </span>
              </div>

              <div className="pn-notif">
                <div className="pn-notif__head">
                  <div className="pn-notif__app">
                    <span className="pn-notif__appicon">
                      <i className="fas fa-bolt" />
                    </span>
                    <span className="pn-notif__appname">App Name</span>
                  </div>
                  <span className="pn-notif__time">now</span>
                </div>

                {previewSrc && imageStatus !== "error" && (
                  <div className="pn-notif__banner">
                    <img
                      src={previewSrc}
                      alt=""
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                )}

                <div className="pn-notif__title">
                  {form.title.trim() || "Notification title"}
                </div>
                <div className="pn-notif__text">
                  {form.body.trim() || "Notification message will appear here."}
                </div>
              </div>

              <div className="pn-phone__hint">Android notification preview</div>
            </div>
          </div>

          {/* --------------------- history --------------------- */}
          <div className="pn-history">
            <div className="pn-history__head">
              <span className="pn-history__title">
                <i className="fas fa-history mr-1" /> Recent messages
                <span className="pn-history__count">{history.length}</span>
              </span>
              {history.length > 0 && (
                <button
                  type="button"
                  className="pn-history__clear"
                  onClick={clearHistory}
                >
                  Clear all
                </button>
              )}
            </div>

            {history.length > 0 && (
              <div className="pn-history__filters">
                <div className="pn-history__search">
                  <i className="fas fa-search" />
                  <input
                    ref={searchRef}
                    type="text"
                    placeholder="Search title, message, user ID…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      type="button"
                      className="pn-history__search-clear"
                      onClick={() => setSearch("")}
                      title="Clear search"
                    >
                      <i className="fas fa-times" />
                    </button>
                  )}
                </div>

                <div className="pn-history__chips">
                  <button
                    type="button"
                    className={
                      "pn-chip" + (filterTarget === "all" ? " is-active" : "")
                    }
                    onClick={() => setFilterTarget("all")}
                  >
                    All
                  </button>
                  {TARGETS.map((t) => {
                    const count = history.filter(
                      (h) => h.target === t.key,
                    ).length;
                    if (!count) return null;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        className={
                          "pn-chip" +
                          (filterTarget === t.key ? " is-active" : "")
                        }
                        onClick={() => setFilterTarget(t.key)}
                      >
                        <i className={t.icon} /> {count}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {historyLoading ? (
              <div className="pn-history__empty">
                <i className="fas fa-spinner fa-spin d-block mb-2" />
                Loading history…
              </div>
            ) : history.length === 0 ? (
              <div className="pn-history__empty">
                No messages sent yet. Your last {HISTORY_LIMIT} sends will
                appear here.
              </div>
            ) : filteredHistory.length === 0 ? (
              <div className="pn-history__empty">
                <i className="fas fa-search d-block mb-2" />
                No messages match your search.
              </div>
            ) : (
              <>
                {(search || filterTarget !== "all") && (
                  <div className="pn-history__results">
                    Showing {filteredHistory.length} of {history.length}
                  </div>
                )}
                <ul className="pn-history__list">
                  {filteredHistory.map((h) => (
                    <li key={h.id} className="pn-history__item">
                      <div className="pn-history__row">
                        <span
                          className="pn-history__item-title"
                          title={h.title}
                        >
                          {h.title}
                        </span>
                        <span className="pn-history__time">
                          {formatWhen(h.at)}
                        </span>
                      </div>
                      <div className="pn-history__body" title={h.body}>
                        {h.body}
                      </div>
                      <div className="pn-history__meta">
                        <span className="pn-history__badge">
                          <i
                            className={
                              TARGETS.find((t) => t.key === h.target)?.icon ||
                              "fas fa-paper-plane"
                            }
                          />{" "}
                          {h.targetLabel}
                          {h.target === "user" && h.userId
                            ? ` · ${h.userId}`
                            : ""}
                        </span>
                        {h.image && (
                          <span className="pn-history__badge pn-history__badge--img">
                            <i className="far fa-image" /> image
                          </span>
                        )}

                        <div className="pn-history__actions">
                          <button
                            type="button"
                            className="pn-history__reuse"
                            onClick={() => reuseEntry(h)}
                            title="Load into form"
                          >
                            <i className="fas fa-redo-alt" />
                          </button>
                          <button
                            type="button"
                            className="pn-history__delete"
                            onClick={() => removeHistoryItem(h.id)}
                            title="Remove from history"
                          >
                            <i className="far fa-trash-alt" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </aside>
      </div>
    </React.Fragment>
  );
};

/* ------------------------------------------------------------------ */
/*  styles                                                             */
/* ------------------------------------------------------------------ */
const styles = `
.pn-wrap{display:grid;grid-template-columns:minmax(0,1fr) 380px;gap:24px;align-items:start;max-width:1140px;margin:0 auto;}
.pn-form{min-width:0;}
.pn-side{position:sticky;top:16px;display:flex;flex-direction:column;gap:20px;}
.pn-side__label{font-size:.72rem;text-transform:uppercase;letter-spacing:.05em;color:#8b95a1;margin-bottom:2px;text-align:center;}

@media (max-width:991.98px){
  .pn-wrap{grid-template-columns:1fr;}
  .pn-side{position:static;}
}

.pn-card{border:1px solid #e6e9ef;border-radius:.6rem;box-shadow:0 2px 10px rgba(15,23,42,.05);}
.pn-card__header{background:#fff;border-bottom:1px solid #eef1f5;padding:14px 18px;display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;}
.pn-header__meta{font-size:.72rem;color:#8b95a1;}
.pn-kbd-hint kbd{background:#eef1f5;border:1px solid #d8dee7;border-radius:3px;padding:1px 5px;font-size:.68rem;font-family:inherit;color:#5b6572;}

.pn-label{font-size:.72rem;text-transform:uppercase;letter-spacing:.05em;color:#8b95a1;margin-bottom:6px;}
.pn-hint{font-size:.78rem;color:#8b95a1;margin:8px 0 0;}
.pn-req{color:#e3342f;}
.pn-optional{color:#a5adba;font-weight:400;}

.pn-targets{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;}
.pn-target{display:flex;flex-direction:column;align-items:center;gap:6px;padding:12px 8px;border:1px solid #d8dee7;border-radius:.5rem;background:#fff;color:#5b6572;cursor:pointer;font-size:.82rem;font-weight:600;transition:all .12s ease;}
.pn-target:hover{border-color:#bfe7f1;color:#17a2b8;}
.pn-target--active{border-color:#17a2b8;background:#e6f6fa;color:#0f8ea5;box-shadow:0 0 0 3px rgba(23,162,184,.15);}
.pn-target i{font-size:1.05rem;}
.pn-target__label{text-align:center;line-height:1.2;}

.pn-charcount{display:block;text-align:right;font-size:.72rem;color:#a5adba;margin-top:2px;transition:color .15s ease;}
.pn-charcount--warn{color:#e0a800;font-weight:600;}

.pn-img-toggle{display:inline-flex;border:1px solid #d8dee7;border-radius:.5rem;overflow:hidden;margin-bottom:10px;background:#f7f9fc;}
.pn-img-toggle__btn{background:transparent;border:0;padding:6px 12px;font-size:.76rem;font-weight:600;color:#5b6572;cursor:pointer;transition:all .12s ease;}
.pn-img-toggle__btn:hover:not(:disabled){color:#17a2b8;background:#eef6f9;}
.pn-img-toggle__btn.is-active{background:#17a2b8;color:#fff;}
.pn-img-toggle__btn:disabled{opacity:.5;cursor:not-allowed;}

.pn-input-wrap{position:relative;}
.pn-input{padding-right:34px;}
.pn-input--error{border-color:#e3342f;}
.pn-input--warn{border-color:#e0a800;}
.pn-input--ok{border-color:#1a9c4a;}
.pn-input__icon{position:absolute;right:10px;top:50%;transform:translateY(-50%);font-size:.9rem;pointer-events:none;}
.pn-input__icon--loading{color:#8b95a1;}
.pn-input__icon--ok{color:#1a9c4a;}
.pn-input__icon--warning{color:#e0a800;}
.pn-input__icon--error{color:#e3342f;}

.pn-img-msg{display:block;font-size:.72rem;margin-top:4px;}
.pn-img-msg--hint{color:#a5adba;}
.pn-img-msg--loading{color:#8b95a1;}
.pn-img-msg--ok{color:#1a9c4a;}
.pn-img-msg--warning{color:#e0a800;}
.pn-img-msg--error{color:#e3342f;}

.pn-drop{border:2px dashed #cfd7e0;border-radius:.6rem;background:#fafbfd;padding:18px;cursor:pointer;transition:border-color .15s ease,background .15s ease;}
.pn-drop:hover{border-color:#17a2b8;background:#f4fafc;}
.pn-drop--busy{cursor:wait;}

.pn-drop__empty{display:flex;flex-direction:column;align-items:center;text-align:center;gap:6px;color:#8b95a1;}
.pn-drop__empty i{font-size:1.6rem;color:#b6bcc4;}
.pn-drop__title{font-size:.9rem;font-weight:600;color:#5b6572;}
.pn-drop__hint{font-size:.75rem;color:#a5adba;}

.pn-drop__filled{display:flex;align-items:center;gap:14px;}
.pn-drop__thumb{position:relative;flex:0 0 auto;width:120px;aspect-ratio:2/1;border-radius:.4rem;overflow:hidden;background:#eef1f5;}
.pn-drop__thumb img{width:100%;height:100%;object-fit:cover;display:block;}
.pn-drop__overlay{position:absolute;inset:0;background:rgba(15,23,42,.55);color:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;font-size:.72rem;font-weight:600;}
.pn-drop__overlay i{font-size:1.1rem;}

.pn-drop__info{flex:1 1 auto;min-width:0;}
.pn-drop__name{font-size:.85rem;font-weight:600;color:#1f2d3d;word-break:break-all;}
.pn-drop__meta{font-size:.75rem;color:#8b95a1;margin-top:2px;}
.pn-drop__status{font-size:.75rem;margin-top:4px;}
.pn-drop__status--busy{color:#17a2b8;font-weight:600;}
.pn-drop__status--ok{color:#1a9c4a;font-weight:600;}
.pn-drop__status--err{color:#e3342f;font-weight:600;}
.pn-drop__remove{margin-top:8px;background:transparent;border:0;color:#e3342f;font-size:.75rem;font-weight:600;padding:0;cursor:pointer;}
.pn-drop__remove:hover{text-decoration:underline;}

.pn-footer{display:flex;align-items:center;justify-content:space-between;margin-top:20px;gap:12px;flex-wrap:wrap;padding-top:16px;border-top:1px dashed #e6e9ef;}
.pn-target-summary{font-size:.8rem;color:#8b95a1;}
.pn-footer__actions{display:flex;gap:8px;}

.pn-phone{width:100%;max-width:340px;margin:0 auto;background:#1f2d3d;border-radius:28px;padding:12px 10px 16px;box-shadow:0 12px 32px rgba(15,23,42,.25);position:relative;}
.pn-phone__notch{width:110px;height:18px;background:#1f2d3d;border-radius:0 0 14px 14px;margin:-12px auto 6px;position:relative;z-index:2;}
.pn-phone__notch::after{content:"";position:absolute;top:5px;left:50%;transform:translateX(-50%);width:44px;height:5px;background:#0b1421;border-radius:3px;}
.pn-phone__screen{background:linear-gradient(160deg,#e8eef5,#dbe4ee);border-radius:18px;padding:10px 10px 14px;min-height:420px;display:flex;flex-direction:column;gap:10px;}
.pn-phone__statusbar{display:flex;align-items:center;justify-content:space-between;font-size:.65rem;color:#5b6572;font-weight:600;padding:2px 8px;}
.pn-phone__statusbar-icons i{margin-left:4px;font-size:.65rem;}

.pn-notif{background:#fff;border-radius:14px;padding:10px 12px 12px;box-shadow:0 3px 10px rgba(15,23,42,.08);}
.pn-notif__head{display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;}
.pn-notif__app{display:flex;align-items:center;gap:6px;}
.pn-notif__appicon{width:20px;height:20px;border-radius:5px;background:linear-gradient(135deg,#17a2b8,#0f8ea5);color:#fff;display:flex;align-items:center;justify-content:center;font-size:.6rem;}
.pn-notif__appname{font-size:.72rem;font-weight:700;color:#5b6572;letter-spacing:.02em;}
.pn-notif__time{font-size:.65rem;color:#a5adba;}
.pn-notif__banner{width:100%;aspect-ratio:2 / 1;background:#eef1f5;border-radius:8px;overflow:hidden;margin-bottom:8px;}
.pn-notif__banner img{width:100%;height:100%;object-fit:cover;display:block;}
.pn-notif__title{font-size:.82rem;font-weight:700;color:#1f2d3d;line-height:1.3;word-break:break-word;}
.pn-notif__text{font-size:.76rem;color:#5b6572;line-height:1.45;margin-top:3px;word-break:break-word;white-space:pre-wrap;}
.pn-phone__hint{text-align:center;font-size:.65rem;color:#7b8591;margin-top:auto;padding-top:6px;}

.pn-history{background:#fff;border:1px solid #e6e9ef;border-radius:.6rem;box-shadow:0 2px 10px rgba(15,23,42,.05);padding:12px 14px 14px;}
.pn-history__head{display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;}
.pn-history__title{font-size:.72rem;text-transform:uppercase;letter-spacing:.05em;color:#8b95a1;font-weight:600;}
.pn-history__count{display:inline-block;margin-left:6px;background:#eef4f7;color:#17a2b8;font-size:.68rem;font-weight:700;border-radius:10px;padding:1px 7px;}
.pn-history__clear{background:transparent;border:0;color:#e3342f;font-size:.72rem;font-weight:600;padding:0;cursor:pointer;}
.pn-history__clear:hover{text-decoration:underline;}
.pn-history__empty{font-size:.78rem;color:#a5adba;text-align:center;padding:20px 6px;font-style:italic;}

.pn-history__filters{margin-bottom:10px;padding-bottom:10px;border-bottom:1px dashed #eef1f5;}
.pn-history__search{position:relative;display:flex;align-items:center;}
.pn-history__search > i.fa-search{position:absolute;left:10px;font-size:.72rem;color:#a5adba;pointer-events:none;}
.pn-history__search input{width:100%;border:1px solid #d8dee7;border-radius:.4rem;padding:6px 28px 6px 28px;font-size:.78rem;background:#fbfcfe;outline:none;transition:all .12s ease;}
.pn-history__search input:focus{border-color:#17a2b8;background:#fff;box-shadow:0 0 0 3px rgba(23,162,184,.12);}
.pn-history__search-clear{position:absolute;right:6px;background:transparent;border:0;color:#a5adba;font-size:.72rem;padding:4px;cursor:pointer;border-radius:3px;}
.pn-history__search-clear:hover{color:#e3342f;background:#fdecec;}

.pn-history__chips{display:flex;flex-wrap:wrap;gap:5px;margin-top:8px;}
.pn-chip{display:inline-flex;align-items:center;gap:4px;border:1px solid #d8dee7;background:#fff;color:#5b6572;border-radius:20px;padding:3px 10px;font-size:.7rem;font-weight:600;cursor:pointer;transition:all .12s ease;}
.pn-chip i{font-size:.62rem;}
.pn-chip:hover{border-color:#bfe7f1;color:#17a2b8;}
.pn-chip.is-active{background:#17a2b8;color:#fff;border-color:#17a2b8;}

.pn-history__results{font-size:.7rem;color:#8b95a1;margin-bottom:6px;font-style:italic;}

.pn-history__list{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:8px;max-height:520px;overflow-y:auto;}
.pn-history__item{border:1px solid #eef1f5;border-radius:.5rem;padding:8px 10px;background:#fbfcfe;transition:border-color .12s ease,box-shadow .12s ease;}
.pn-history__item:hover{border-color:#bfe7f1;box-shadow:0 2px 8px rgba(23,162,184,.08);}
.pn-history__row{display:flex;align-items:baseline;justify-content:space-between;gap:8px;}
.pn-history__item-title{font-size:.82rem;font-weight:700;color:#1f2d3d;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.pn-history__time{font-size:.68rem;color:#a5adba;flex:0 0 auto;}
.pn-history__body{font-size:.75rem;color:#5b6572;margin-top:3px;line-height:1.4;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
.pn-history__meta{display:flex;align-items:center;gap:6px;margin-top:6px;flex-wrap:wrap;}
.pn-history__badge{display:inline-flex;align-items:center;gap:4px;font-size:.66rem;color:#5b6572;background:#eef4f7;border-radius:10px;padding:2px 7px;font-weight:600;}
.pn-history__badge i{font-size:.62rem;color:#17a2b8;}
.pn-history__badge--img{background:#fdf3e6;color:#b8720c;}
.pn-history__badge--img i{color:#e0a800;}

.pn-history__actions{margin-left:auto;display:flex;gap:2px;}
.pn-history__reuse,.pn-history__delete{background:transparent;border:0;font-size:.72rem;padding:3px 6px;border-radius:.3rem;cursor:pointer;transition:background .12s ease;}
.pn-history__reuse{color:#17a2b8;}
.pn-history__reuse:hover{background:#e6f6fa;}
.pn-history__delete{color:#a5adba;}
.pn-history__delete:hover{background:#fdecec;color:#e3342f;}

@media (max-width:575.98px){
  .pn-targets{grid-template-columns:1fr;}
  .pn-target{flex-direction:row;justify-content:flex-start;padding:10px 12px;}
  .pn-target__label{text-align:left;}
  .pn-footer__actions{width:100%;}
  .pn-footer__actions .btn{flex:1;}
  .pn-drop__filled{flex-direction:column;align-items:flex-start;}
  .pn-drop__thumb{width:100%;}
}
`;

export default PushNotification;
